package org.moxultimate.topwithmox.libraries

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

/**
 * Demonstrates kobweb:silk-foundation — the unstyled Silk primitive layer.
 *
 * [kobweb-silk] (the full component library) is built on top of silk-foundation.
 * Adding silk-foundation explicitly is useful when:
 * - You want to build a custom component library without pulling in all Silk styles.
 * - You need headless primitives like [Overlay] with full control over styling.
 *
 * Key APIs:
 * - [CssStyle] — declare reusable, theme-aware CSS styles attached to Modifiers.
 * - [Overlay] — a full-screen backdrop layer used for modals, drawers, tooltips.
 * - [OverlayVars] — CSS variables for customising the overlay appearance.
 * - [CssStyle.base] — shorthand for a style that applies at all breakpoints.
 *
 * Since this project already depends on kobweb-silk (which includes silk-foundation
 * transitively), the APIs below are available without any additional imports.
 */

/** Custom dialog style built with silk-foundation's [CssStyle]. */
val DialogBoxStyle = CssStyle.base {
    Modifier
        .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
        .borderRadius(8.px)
        .padding(2.cssRem)
        .minWidth(20.cssRem)
}

@Composable
fun SilkFoundationSample() {
    var showModal by remember { mutableStateOf(false) }
    var clickCount by remember { mutableStateOf(0) }

    Column(Modifier.gap(1.cssRem)) {
        SpanText("Overlay / modal demo — clicks outside: $clickCount")

        Button(onClick = { showModal = true }) {
            Text("Open modal")
        }

        // ── Overlay — silk-foundation headless backdrop ───────────────────────
        // Overlay renders a full-screen layer on top of all other content.
        // Clicking it fires the onClick handler — commonly used to dismiss modals.
        if (showModal) {
            Overlay(
                modifier = Modifier
                    .setVariable(OverlayVars.BackgroundColor,
                        com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.5f))
                    .onClick { clickCount++; showModal = false },
            ) {
                // Stop click propagation so clicking the dialog doesn't dismiss it
                Box(
                    DialogBoxStyle.toModifier()
                        .align(Alignment.Center)
                        .onClick { it.stopPropagation() },
                    contentAlignment = Alignment.Center,
                ) {
                    Column(Modifier.gap(1.cssRem)) {
                        SpanText("Modal content here")
                        Button(onClick = { showModal = false }) {
                            Text("Close")
                        }
                    }
                }
            }
        }
    }
}
